/**
 * A skill's instructions, typed in place, with `@` mentions of library
 * documents and other skills shown as chips. The textarea's own text is
 * transparent; a mirror layer behind it draws the same characters with the
 * chips coloured, so the glyphs line up (the Composer's technique). Typing
 * `@` or pressing the @ button opens a picker under the `@`; arrows, Enter or
 * Tab and Esc work as in the Composer. A Backspace into a chip removes it
 * whole. The stored text keeps markdown links (`instructionMentions`).
 *
 * Renders two `ListCard` rows: the editor and its footer (the @ button, then
 * `footer`). While the text is empty, `emptyAction` sits over the bottom-left
 * of the box (only it takes clicks; the rest still focuses the textarea).
 */
import { AtSign, FileText, Sparkles } from "lucide-react";
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FC,
  type KeyboardEvent,
  type ReactNode
} from "react";

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverAnchor,
  PopoverContent
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type {
  FirmLibraryEntry,
  FirmSkill
} from "@/features/firmKnowledge/types";
import {
  activeMentionAt,
  filterCandidates,
  insertMention,
  instructionSegments,
  mentionedIds,
  parseInstructions,
  reconcileInstructions,
  toInstructions,
  type ActiveMention,
  type InstructionCandidate,
  type InstructionModel
} from "./instructionMentions";

export const INSTRUCTIONS_PLACEHOLDER =
  "Write the steps… Type @ to point at a library document or automation.";

/** Shared by the textarea and its mirror so the glyphs line up. */
const METRICS = "px-3 py-3 text-md leading-6 whitespace-pre-wrap break-words";

const CHIP: Record<InstructionCandidate["kind"], string> = {
  entry: "bg-info-soft text-info",
  skill: "bg-purple-soft text-purple"
};

type Props = {
  value: string;
  onChange: (value: string) => void;
  /** The ids the text mentions now, whenever they change. */
  onMentionsChange?: (ids: { entries: string[]; skills: string[] }) => void;
  library: readonly FirmLibraryEntry[];
  /** Other skills it may point at (not this one). */
  skills: readonly FirmSkill[];
  /** After the @ button in the footer row. */
  footer?: ReactNode;
  /** Over the box while it is empty: a call to action and its hint. */
  emptyAction?: ReactNode;
};

export const InstructionsEditor: FC<Props> = ({
  value,
  onChange,
  onMentionsChange,
  library,
  skills,
  footer,
  emptyAction
}) => {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const mirrorRef = useRef<HTMLDivElement | null>(null);
  const [model, setModel] = useState<InstructionModel>(() =>
    parseInstructions(value)
  );
  const modelRef = useRef(model);
  const emitted = useRef(value);
  const [mention, setMention] = useState<ActiveMention | null>(null);
  const [highlight, setHighlight] = useState(0);
  const pendingCaret = useRef<number | null>(null);

  // Someone else wrote the instructions (the builder, a restore): re-read them.
  useEffect(() => {
    if (value === emitted.current) return;
    emitted.current = value;
    const next = parseInstructions(value);
    modelRef.current = next;
    setModel(next);
    setMention(null);
  }, [value]);

  const mentionKey = JSON.stringify(mentionedIds(model));
  const onMentionsRef = useRef(onMentionsChange);
  onMentionsRef.current = onMentionsChange;
  useEffect(() => {
    onMentionsRef.current?.(JSON.parse(mentionKey));
  }, [mentionKey]);

  const candidates = useMemo<InstructionCandidate[]>(
    () => [
      ...library.map((e) => ({
        kind: "entry" as const,
        id: e.id,
        label: e.name
      })),
      ...skills.map((s) => ({
        kind: "skill" as const,
        id: s.id,
        label: s.title || s.slug
      }))
    ],
    [library, skills]
  );
  const matches = useMemo(
    () => (mention ? filterCandidates(candidates, mention.query) : []),
    [candidates, mention]
  );
  const open = Boolean(mention && matches.length);
  const showEmptyAction = Boolean(emptyAction) && !model.display.trim();

  const commit = (next: InstructionModel, caret?: number) => {
    modelRef.current = next;
    setModel(next);
    const text = toInstructions(next);
    emitted.current = text;
    onChange(text);
    if (caret !== undefined) pendingCaret.current = caret;
  };

  const syncMention = () => {
    const el = textareaRef.current;
    if (!el) return;
    const next = activeMentionAt(
      el.value,
      el.selectionStart ?? el.value.length,
      modelRef.current.mentions
    );
    setMention((prev) =>
      prev?.start === next?.start && prev?.query === next?.query ? prev : next
    );
    if (!next || next.query !== mention?.query) setHighlight(0);
  };

  // Caret moves land once React has written the new text into the textarea.
  useLayoutEffect(() => {
    const caret = pendingCaret.current;
    const el = textareaRef.current;
    if (caret === null || !el) return;
    pendingCaret.current = null;
    el.focus();
    el.setSelectionRange(caret, caret);
    syncMention();
  });

  const onTextChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.currentTarget.value;
    const out = reconcileInstructions(modelRef.current, text);
    commit(out.model, out.model.display !== text ? out.caret : undefined);
    if (out.model.display === text) syncMention();
    else setMention(null);
  };

  const choose = (c: InstructionCandidate) => {
    const el = textareaRef.current;
    if (!el || !mention) return;
    setMention(null);
    const out = insertMention(
      modelRef.current,
      mention.start,
      el.selectionStart ?? el.value.length,
      { kind: c.kind, id: c.id, label: c.label }
    );
    commit(out.model, out.caret);
  };

  const startMention = () => {
    const el = textareaRef.current;
    const cur = modelRef.current.display;
    const at =
      el && document.activeElement === el ? el.selectionStart : cur.length;
    const before = cur.slice(0, at);
    const sep = before && !/\s$/.test(before) ? " " : "";
    const text = `${before}${sep}@${cur.slice(at)}`;
    commit(
      reconcileInstructions(modelRef.current, text).model,
      at + sep.length + 1
    );
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => (h + 1) % matches.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => (h - 1 + matches.length) % matches.length);
    } else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      choose(matches[highlight] ?? matches[0]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      setMention(null);
    }
  };

  // The mirror, with a zero-width anchor at the `@` being typed.
  const segments = instructionSegments(model);
  const anchorAt = mention?.start ?? null;
  let offset = 0;
  const mirror: ReactNode[] = [];
  const anchor = (
    <PopoverAnchor asChild key="anchor">
      <span />
    </PopoverAnchor>
  );
  segments.forEach((seg, i) => {
    const end = offset + seg.text.length;
    if (seg.type === "mention") {
      mirror.push(
        <span
          key={i}
          data-testid={`instructions-chip-${seg.target.kind}`}
          className={cn("-mx-0.5 rounded px-0.5", CHIP[seg.target.kind])}
        >
          {seg.text}
        </span>
      );
    } else if (anchorAt !== null && anchorAt >= offset && anchorAt < end) {
      const cut = anchorAt - offset;
      mirror.push(<span key={`${i}a`}>{seg.text.slice(0, cut)}</span>);
      mirror.push(anchor);
      mirror.push(<span key={`${i}b`}>{seg.text.slice(cut)}</span>);
    } else {
      mirror.push(<span key={i}>{seg.text}</span>);
    }
    offset = end;
  });

  return (
    <Popover open={open}>
      <div className="relative">
        <div
          ref={mirrorRef}
          aria-hidden
          data-testid="instructions-mirror"
          className={cn(
            METRICS,
            "pointer-events-none absolute inset-0 overflow-hidden text-fg"
          )}
        >
          {mirror}
          {"\u200b"}
        </div>
        <textarea
          ref={textareaRef}
          value={model.display}
          onChange={onTextChange}
          onKeyDown={onKeyDown}
          onKeyUp={(e) => {
            if (
              !["ArrowDown", "ArrowUp", "Enter", "Tab", "Escape"].includes(
                e.key
              )
            )
              syncMention();
          }}
          onClick={syncMention}
          onScroll={(e) => {
            if (mirrorRef.current)
              mirrorRef.current.scrollTop = e.currentTarget.scrollTop;
          }}
          onBlur={() => setTimeout(() => setMention(null), 120)}
          placeholder={INSTRUCTIONS_PLACEHOLDER}
          aria-label="Instructions"
          aria-autocomplete="list"
          className={cn(
            METRICS,
            "relative z-10 field-sizing-content w-full resize-none bg-transparent text-transparent caret-fg outline-none placeholder:text-fg-4",
            showEmptyAction ? "min-h-36" : "min-h-28"
          )}
          data-testid="skill-instructions-input"
        />
        {showEmptyAction ? (
          <div
            className="pointer-events-none absolute inset-x-3 bottom-3 z-20 flex flex-wrap items-center gap-x-3 gap-y-1.5"
            data-testid="instructions-empty-action"
          >
            {emptyAction}
          </div>
        ) : null}
      </div>
      <PopoverContent
        align="start"
        side="bottom"
        sideOffset={6}
        className="w-72 gap-0 p-1"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <div
          role="listbox"
          aria-label="Mention"
          data-testid="instructions-picker"
        >
          {matches.map((c, i) => {
            const header = i === 0 || matches[i - 1].kind !== c.kind;
            const Icon = c.kind === "entry" ? FileText : Sparkles;
            return (
              <div key={`${c.kind}:${c.id}`}>
                {header ? (
                  <div
                    role="presentation"
                    className="px-2 pt-1.5 pb-0.5 text-sm text-fg-3"
                  >
                    {c.kind === "entry" ? "Library" : "Automations"}
                  </div>
                ) : null}
                <button
                  type="button"
                  role="option"
                  aria-selected={i === highlight}
                  data-kind={c.kind}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    choose(c);
                  }}
                  onMouseEnter={() => setHighlight(i)}
                  className={cn(
                    "flex w-full items-center gap-2 rounded-md px-2 py-1 text-left text-base text-fg",
                    i === highlight ? "bg-fill-2" : "hover:bg-fill-1"
                  )}
                >
                  <Icon className="size-3.5 shrink-0 text-fg-3" />
                  <span className="min-w-0 flex-1 truncate">{c.label}</span>
                </button>
              </div>
            );
          })}
        </div>
      </PopoverContent>
      <div className="flex min-w-0 items-center gap-2 px-3 py-2 text-sm text-fg-2">
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          className="-ml-1"
          aria-label="Mention a Document or Automation"
          title="Mention a Document or Automation"
          onMouseDown={(e) => e.preventDefault()}
          onClick={startMention}
          data-testid="instructions-mention"
        >
          <AtSign />
        </Button>
        {footer}
      </div>
    </Popover>
  );
};
